import { create } from 'zustand'
import { api } from './api'
import type { Message, Part } from './api'
import { realtime } from './realtime'
import type { Envelope, PartDelta } from './realtime'

// Normalised message state, kept outside React Query because streaming deltas
// arrive dozens of times a second and reconciling them through a query cache
// would re-render the whole list on every token.

interface State {
  /** messages[conversationId] is ordered by seq ascending. */
  messages: Record<string, Message[]>
  /** Conversations with at least one run in flight, for the "typing…" indicator. */
  streaming: Record<string, boolean>
  /** Run ids currently in flight per conversation, so "stop generating" knows
   *  what to stop. A direct chat has at most one; a group @mention or a
   *  broadcast can put several agents' runs in flight from a single send. */
  activeRunIds: Record<string, string[]>

  setMessages: (conversationId: string, messages: Message[]) => void
  mergeMessages: (conversationId: string, messages: Message[]) => void
  applyEnvelope: (e: Envelope) => void
  clear: (conversationId: string) => void
}

/** addRunID adds runId if it is not already present, returning the same
 *  array reference when nothing changed — the identity stability matters for
 *  anything that keys a React effect off this array. */
function addRunID(ids: string[] | undefined, runId?: string): string[] {
  const cur = ids ?? []
  if (!runId || cur.includes(runId)) return cur
  return [...cur, runId]
}

/** removeRunID is addRunID's inverse. */
function removeRunID(ids: string[] | undefined, runId?: string): string[] {
  const cur = ids ?? []
  if (!runId || !cur.includes(runId)) return cur
  return cur.filter((id) => id !== runId)
}

/** upsert keeps a conversation's messages sorted and de-duplicated by id.
 *
 * A 'streaming' incoming snapshot is never allowed to overwrite a message
 * already held in a terminal status. It sounds like it shouldn't be able to
 * — but the send response and the socket both eventually deliver every
 * message, on independent paths with no ordering guarantee between them (see
 * mergeMessages' doc comment for the same race on run tracking), and a send
 * response is a snapshot from the moment the run *started*: still
 * 'streaming', with whatever parts existed at that instant. If it lands
 * after message.done already wrote the finished text, applying it anyway
 * would revert a complete message back into a blank streaming placeholder
 * that nothing will ever finish again. */
function upsert(list: Message[], incoming: Message): Message[] {
  const i = list.findIndex((m) => m.id === incoming.id)
  if (i >= 0) {
    if (list[i].status !== 'streaming' && incoming.status === 'streaming') return list
    const next = list.slice()
    next[i] = { ...next[i], ...incoming }
    return next
  }
  const next = [...list, incoming]
  next.sort((a, b) => a.seq - b.seq)
  return next
}

/** mergeList folds a page of messages (REST or a resync catch-up) into what's
 *  already held, rather than trusting the page to be the whole truth. The
 *  initial transcript load races the socket by design — a `part.delta` or
 *  `message.created` can land while `listMessages` is still in flight — and
 *  whichever resolves second must not erase what the other already applied. */
function mergeList(list: Message[], incoming: Message[]): Message[] {
  let next = list
  for (const m of incoming) next = upsert(next, m)
  return next
}

/** resyncConversation is the client half of EventResync: the server drops a
 *  subscriber that falls behind on one conversation's event stream rather
 *  than buffering without bound (see EventResync in envelope.go), so the
 *  only way to close the resulting gap is to go back to REST. Fire-and-forget
 *  from applyEnvelope — the socket has already told us it can't be trusted
 *  for this range, so there is nothing useful to compute synchronously. */
async function resyncConversation(conversationId: string) {
  const afterSeq = realtime.getCursor(conversationId)
  try {
    const messages = await api.listMessages(conversationId, afterSeq)
    if (messages.length === 0) return
    useMessages.getState().mergeMessages(conversationId, messages)
    const maxSeq = messages.reduce((max, m) => Math.max(max, m.seq), afterSeq)
    realtime.setCursor(conversationId, maxSeq)
  } catch {
    // Best effort: a failed catch-up leaves the gap in place until the next
    // resync or a full reconnect, both of which retry this same path.
  }
}

/** applyDelta appends streamed text to the addressed part, creating it if the
 *  part.created event was missed — a dropped delta must not strand the rest. */
function applyDelta(parts: Part[], d: PartDelta): Part[] {
  const i = parts.findIndex((p) => p.index === d.index)
  if (i < 0) {
    // Annotated rather than inferred: inside an array literal TypeScript widens
    // `status` to string, which does not satisfy Part and silently loses the
    // check that this is a valid part status at all.
    const created: Part = {
      index: d.index,
      kind: (d.kind as Part['kind']) ?? 'text',
      text: d.text,
      status: 'streaming',
    }
    return [...parts, created].sort((a, b) => a.index - b.index)
  }
  const next = parts.slice()
  next[i] = { ...next[i], text: (next[i].text ?? '') + d.text }
  return next
}

export const useMessages = create<State>((set) => ({
  messages: {},
  streaming: {},
  activeRunIds: {},

  // A wholesale replace here would let a REST response that started before
  // the socket connected clobber messages the socket already delivered
  // while the fetch was in flight — the loaded page is a snapshot, not the
  // full story, so it has to merge like everything else that touches
  // `messages`.
  setMessages: (conversationId, messages) =>
    set((s) => ({
      messages: { ...s.messages, [conversationId]: mergeList(s.messages[conversationId] ?? [], messages) },
    })),

  // A streaming agent message folded in here — from the send response, before
  // the socket has necessarily connected — registers as an active run too.
  // Without this, Stop and the "typing…" indicator would lag behind the
  // socket catching up, and a page load slow enough that Clerk is still
  // minting its first token would show "Send" on a conversation that has, in
  // fact, already started answering. A resync catch-up calls this too, but a
  // resynced message is never `streaming` — it is a finished part of the
  // transcript — so the check does not fire spuriously there.
  //
  // The send response is a snapshot from the moment the run started, and it
  // can arrive after the socket has already delivered that same run's whole
  // lifecycle (created, deltas, done) — the response is just another HTTP
  // request competing for the server's attention, and a fast agent (or a
  // busy server) can finish before it returns. Trusting `status ===
  // 'streaming'` unconditionally would then resurrect a run that already
  // finished and will never be marked done again, leaving Stop stuck forever
  // — reproduced live against a sniff-mode group room, where three runs kick
  // off from one send and the fastest routinely beats its own HTTP response.
  // Checking the message already held for this id is what catches it: if the
  // socket got there first, that message's status is no longer 'streaming'.
  mergeMessages: (conversationId, incoming) =>
    set((s) => {
      const existing = s.messages[conversationId] ?? []
      let activeRunIds = s.activeRunIds[conversationId] ?? []
      for (const m of incoming) {
        if (m.role !== 'agent' || m.status !== 'streaming') continue
        const current = existing.find((e) => e.id === m.id)
        if (current && current.status !== 'streaming') continue
        activeRunIds = addRunID(activeRunIds, m.run_id)
      }
      return {
        messages: { ...s.messages, [conversationId]: mergeList(existing, incoming) },
        streaming: { ...s.streaming, [conversationId]: activeRunIds.length > 0 },
        activeRunIds: { ...s.activeRunIds, [conversationId]: activeRunIds },
      }
    }),

  clear: (conversationId) =>
    set((s) => {
      const messages = { ...s.messages }
      const streaming = { ...s.streaming }
      const activeRunIds = { ...s.activeRunIds }
      delete messages[conversationId]
      delete streaming[conversationId]
      delete activeRunIds[conversationId]
      return { messages, streaming, activeRunIds }
    }),

  applyEnvelope: (e) =>
    set((s) => {
      const cid = e.conversation_id
      if (!cid) return s

      switch (e.type) {
        case 'message.created': {
          const msg = e.data as Message
          if (!msg?.id) return s
          // Only an agent-authored message is evidence of a run in progress.
          // A user message.created fires whenever the user (or another of
          // their devices) sends while an agent is still mid-reply, and must
          // not stomp the indicator off — nothing else turns it back on,
          // since the following events are just part.delta.
          if (msg.role !== 'agent') {
            return { messages: { ...s.messages, [cid]: upsert(s.messages[cid] ?? [], msg) } }
          }
          const activeRunIds = { ...s.activeRunIds, [cid]: addRunID(s.activeRunIds[cid], e.run_id) }
          return {
            messages: { ...s.messages, [cid]: upsert(s.messages[cid] ?? [], msg) },
            streaming: { ...s.streaming, [cid]: activeRunIds[cid].length > 0 },
            activeRunIds,
          }
        }

        case 'part.delta': {
          const d = e.data as PartDelta
          const list = s.messages[cid] ?? []
          const i = list.findIndex((m) => m.id === e.message_id)
          if (i < 0 || !d) return s

          const next = list.slice()
          next[i] = { ...next[i], parts: applyDelta(next[i].parts ?? [], d) }
          return { messages: { ...s.messages, [cid]: next } }
        }

        case 'part.done': {
          const d = e.data as { index: number }
          const list = s.messages[cid] ?? []
          const i = list.findIndex((m) => m.id === e.message_id)
          if (i < 0 || !d) return s

          const parts = (list[i].parts ?? []).map((p) =>
            p.index === d.index ? { ...p, status: 'done' as const } : p,
          )
          const next = list.slice()
          next[i] = { ...next[i], parts }
          return { messages: { ...s.messages, [cid]: next } }
        }

        case 'message.done': {
          const msg = e.data as Message
          const list = msg?.id ? upsert(s.messages[cid] ?? [], msg) : (s.messages[cid] ?? [])
          // Removing by id rather than clearing the whole set is what makes
          // this correct for a group or broadcast: one agent finishing must
          // not touch the run ids of the others still mid-reply in the same
          // conversation.
          const activeRunIds = { ...s.activeRunIds, [cid]: removeRunID(s.activeRunIds[cid], e.run_id) }
          return {
            messages: { ...s.messages, [cid]: list },
            streaming: { ...s.streaming, [cid]: activeRunIds[cid].length > 0 },
            activeRunIds,
          }
        }

        case 'run.status': {
          const d = e.data as { status: string }
          const done = ['done', 'error', 'aborted', 'interrupted'].includes(d?.status ?? '')
          const activeRunIds = {
            ...s.activeRunIds,
            [cid]: done ? removeRunID(s.activeRunIds[cid], e.run_id) : addRunID(s.activeRunIds[cid], e.run_id),
          }
          return {
            streaming: { ...s.streaming, [cid]: activeRunIds[cid].length > 0 },
            activeRunIds,
          }
        }

        case 'typing':
          return { streaming: { ...s.streaming, [cid]: true } }

        case 'resync':
          // Nothing here is synchronously computable: the point of resync is
          // that our local state for this conversation can no longer be
          // trusted to fill in the gap. Kick off the REST catch-up and leave
          // this update a no-op; mergeMessages lands the result when it's back.
          void resyncConversation(cid)
          return s

        default:
          return s
      }
    }),
}))
